import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { SpousalWaiverTypeEnum } from '../models/spousal-waivers/spousal-waiver-type.enum';
import { GoverningJurisdictionTypeId } from '../models/enum/shared/governing-jurisdiction-type-id';
import { spousalWaiverRequiredValidator } from '../views/records/components/members/ip-member-record/components/ip-member-record-variable-benefit-configuration/validators/spousal-waiver-required-validator';
import { VariableBenefitGoverningJurisdictionDetail } from '../models/submissions/variable-benefit-governing-jurisdiction-detail';
import { isoDateStringValidator } from '../validators/iso-date-string-validator';

export type SpousalWaiverEntryFormGroup = FormGroup<{
  id: FormControl<string | null>;
  spousalDeclaration: FormControl<File[] | null>;
  spousalWaiverDateSigned: FormControl<string | null>;
  spousalWaiverEffectiveDate: FormControl<string | null>;
  spousalWaiverType: FormControl<SpousalWaiverTypeEnum | null>;
}>;

export type JurisdictionSpousalWaiverFormGroup = FormGroup<{
  governingJurisdictionId: FormControl<number | null>;
  waivers: FormArray<SpousalWaiverEntryFormGroup>;
}>;

export interface SpousalWaiverEntrySeed {
  id?: string | null;
  spousalDeclaration?: File | null;
  spousalWaiverDateSigned?: string | null;
  spousalWaiverEffectiveDate?: string | null;
  spousalWaiverType?: SpousalWaiverTypeEnum | null;
}

export function buildSpousalWaiverEntryFormGroup(seed: SpousalWaiverEntrySeed = {}): SpousalWaiverEntryFormGroup {
  return new FormGroup(
    {
      id: new FormControl<string | null>(seed?.id ?? null),
      spousalDeclaration: new FormControl<File[] | null>(seed?.spousalDeclaration ? [seed.spousalDeclaration] : null),
      spousalWaiverDateSigned: new FormControl<string | null>(seed.spousalWaiverDateSigned ?? null, {
        validators: [isoDateStringValidator]
      }),
      spousalWaiverEffectiveDate: new FormControl<string | null>(seed.spousalWaiverEffectiveDate ?? null, {
        validators: [isoDateStringValidator]
      }),
      spousalWaiverType: new FormControl<SpousalWaiverTypeEnum | null>(seed.spousalWaiverType ?? null)
    },
    { validators: spousalWaiverRequiredValidator }
  );
}

export function buildSpousalWaiverFormGroup(
  detail: VariableBenefitGoverningJurisdictionDetail
): JurisdictionSpousalWaiverFormGroup {
  const existingWaivers = detail.spousalWaivers ?? [];

  // Seed one entry per existing waiver, or a single empty entry when the jurisdiction has none.
  const seededEntries = existingWaivers.length
    ? existingWaivers.map(waiver =>
        buildSpousalWaiverEntryFormGroup({
          id: waiver.id,
          spousalWaiverDateSigned: waiver.signedDate,
          spousalWaiverEffectiveDate: waiver.effectiveDate,
          spousalWaiverType: waiver.waiverTypeId
        })
      )
    : [buildSpousalWaiverEntryFormGroup()];

  return new FormGroup({
    governingJurisdictionId: new FormControl<number | null>(detail.governingJurisdictionId),
    waivers: new FormArray<SpousalWaiverEntryFormGroup>(seededEntries)
  });
}

export function buildEmptySpousalWaiverFormGroup(
  governingJurisdictionId: GoverningJurisdictionTypeId | null
): JurisdictionSpousalWaiverFormGroup {
  return new FormGroup({
    governingJurisdictionId: new FormControl<number | null>(governingJurisdictionId),
    waivers: new FormArray<SpousalWaiverEntryFormGroup>([buildSpousalWaiverEntryFormGroup()])
  });
}
